import type { Pitch, PostPublic } from "../desk-records";

export function notesForPost(postId: string): string {
  return JSON.stringify({ post_id: postId });
}

/**
 * SHIPS ARE UPVOTES, NOT THE NET SCORE. The forum's vote stores -1/0/1 and
 * reports `score` (net) and `vote_count` (votes cast), so the upvotes are
 * `(vote_count + score) / 2`; a stray downvote in the forum then costs the
 * pitch nothing on the Desk. Older rows without `vote_count` fall back to
 * the net score, floored at zero.
 */
function shipsFromVote(vote: {
  readonly score: number;
  readonly vote_count?: number | null;
}): number {
  if (typeof vote.vote_count === "number")
    return Math.max(0, Math.round((vote.vote_count + vote.score) / 2));

  return Math.max(0, vote.score);
}

/** The agent's `normalise()`: links out, whitespace collapsed, case folded. */
function normaliseText(text: string): string {
  return text
    .replace(/https?:\/\/\S+/gi, "")
    .replace(/&amp;/g, "&")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

const entryPostPrefix = "desk-entry:";

/** Forum body for a pitch that has no post yet. Text matches on reload; a clip with no words matches by version. */
export function forumBodyForPitch({
  text,
  policyVersionId,
}: {
  readonly text: string;
  readonly policyVersionId: string;
}): string {
  const trimmed = text.trim();
  return trimmed.length > 0 ? trimmed : `${entryPostPrefix}${policyVersionId}`;
}

function policyVersionInForumBody(
  body: string | null | undefined,
): string | null {
  if (!body?.startsWith(entryPostPrefix)) return null;

  const id = body.slice(entryPostPrefix.length);

  if (id.length === 0 || /\s/.test(id)) return null;

  return id;
}

export function linkPitchPosts({
  pitches,
  posts,
}: {
  readonly pitches: readonly Pitch[];
  readonly posts: readonly PostPublic[];
}): Pitch[] {
  const postsById = new Map(posts.map((post) => [post.id, post]));
  const postsByText = new Map<string, PostPublic>();
  const postsByEntry = new Map<string, PostPublic>();

  // The oldest matching post keeps the votes when someone reposts the same words.
  for (const post of posts.toSorted((a, b) =>
    a.created_at.localeCompare(b.created_at),
  )) {
    const key = normaliseText(post.body ?? post.title);

    if (key && !postsByText.has(key)) postsByText.set(key, post);

    const version = policyVersionInForumBody(post.body);

    if (version && !postsByEntry.has(version)) postsByEntry.set(version, post);
  }

  return pitches.map((pitch) => {
    const post =
      (pitch.postId ? postsById.get(pitch.postId) : undefined) ??
      (pitch.text ? postsByText.get(normaliseText(pitch.text)) : undefined) ??
      postsByEntry.get(pitch.policyVersionId);

    if (!post) return pitch;

    return {
      ...pitch,
      postId: post.id,
      authorUserId: post.author.type === "user" ? post.author.user_id : null,
      ships: shipsFromVote(post),
    };
  });
}

export function postIdFromNotes(
  notes: string | null | undefined,
): string | null {
  if (!notes) return null;

  try {
    const parsed: unknown = JSON.parse(notes);

    if (!parsed || typeof parsed !== "object" || !("post_id" in parsed))
      return null;

    return typeof parsed.post_id === "string" &&
      parsed.post_id.startsWith("post_")
      ? parsed.post_id
      : null;
  } catch {
    return null;
  }
}
